import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Stack,
  Tooltip,
  InputAdornment,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { camposDeMes } from '../../config/planoContas';

const moeda = (valor) => {
  const numero = Number(valor) || 0;
  return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

/**
 * Um grupo do plano de contas dentro da página de UM mês.
 *
 * Era uma grade de N colunas, uma por mês. A grade não sobrevive a 6 nem a 12:
 * a aritmética do MUI (4 da conta + 2 por mês + 2 do remover) só fecha as 12
 * colunas com exatamente três meses, e no celular — que é o aparelho do
 * empresário — doze colunas viram 384 campos empilhados sem hierarquia
 * nenhuma. Agora o eixo da tela é o mês, que é também o eixo do documento que
 * ele tem na mão: o extrato de abril, não a série anual da conta "Aluguel".
 */
const GrupoContas = ({
  grupo,
  contas,
  mes,
  quantidadeMeses,
  onChange,
  mesAnterior = null,
  aoRepetirGrupo = null
}) => {
  const handleValorChange = (codigo, valor) => {
    // Aceita apenas número, vírgula e ponto — evita lixo no cálculo
    const limpo = valor.replace(/[^\d.,-]/g, '').replace(',', '.');
    onChange(contas.map((c) => (c.codigo === codigo ? { ...c, [mes.campo]: limpo } : c)));
  };

  const handleNomeChange = (codigo, nome) => {
    onChange(contas.map((c) => (c.codigo === codigo ? { ...c, nome } : c)));
  };

  const adicionarConta = () => {
    const extras = contas.filter((c) => c.custom).length + 1;
    onChange([
      ...contas,
      {
        codigo: `${grupo.codigo}.x${extras}`,
        nome: '',
        custom: true,
        // A conta nasce com os campos de TODOS os meses da janela, e não só do
        // mês em que foi criada: sem isso a conta acrescentada em maio ficaria
        // sem campo em abril, e o transform leria `undefined` no mês vizinho.
        ...Object.fromEntries(camposDeMes(quantidadeMeses).map((campo) => [campo, '']))
      }
    ]);
  };

  const removerConta = (codigo) => {
    onChange(contas.filter((c) => c.codigo !== codigo));
  };

  const total = contas.reduce((soma, conta) => soma + (Number(conta[mes.campo]) || 0), 0);
  const temAnterior = Boolean(mesAnterior && aoRepetirGrupo);

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1.5
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {grupo.titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {grupo.ajuda} Deixe em branco o que a empresa não tem.
            </Typography>
          </Box>

          {temAnterior && (
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => aoRepetirGrupo(grupo.id)}
              sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
            >
              Repetir de {mesAnterior.curto}
            </Button>
          )}
        </Box>

        <Stack spacing={1.5}>
          {contas.map((conta) => (
            <Box
              key={conta.codigo}
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {conta.custom ? (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nome da conta"
                    placeholder="Ex.: Seguro do imóvel"
                    value={conta.nome}
                    onChange={(e) => handleNomeChange(conta.codigo, e.target.value)}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label={`Valor em ${mes.curto}`}
                    placeholder="0,00"
                    value={conta[mes.campo] ?? ''}
                    onChange={(e) => handleValorChange(conta.codigo, e.target.value)}
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>
                    }}
                  />
                </Stack>
              ) : (
                <Tooltip title={`Código ${conta.codigo} no plano de contas`} placement="top-start">
                  <TextField
                    fullWidth
                    size="small"
                    label={conta.nome}
                    placeholder="deixe em branco se não teve"
                    value={conta[mes.campo] ?? ''}
                    onChange={(e) => handleValorChange(conta.codigo, e.target.value)}
                    inputProps={{ inputMode: 'decimal' }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>
                    }}
                  />
                </Tooltip>
              )}

              {conta.custom && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removerConta(conta.codigo)}
                  aria-label={`Remover ${conta.nome || 'conta'}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>

        <Button startIcon={<AddCircleOutlineIcon />} onClick={adicionarConta} sx={{ mt: 1.5 }}>
          Adicionar outra conta neste grupo
        </Button>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Total do grupo em {mes.longo}
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {moeda(total)}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default GrupoContas;
